/* Correct a refill (2026-10-28): the classic "Were the figures wrong?" panel in
   the console, opened the same way — only from an alert that says a refill's
   figures disagree with something (alertInvitesRefuelCorrection), never as an
   edit button on every row. Litres, cost and, new here, the odometer reading:
   the odometer verdicts ("lower than the fill before") are about the reading,
   and classic could only change litres and cost. The driver's own figures stay
   on the refill (services/refuelCorrection keeps them in `original`). */
import React, { useMemo, useState } from 'react';
import { ConsoleModal, fieldInput, fieldSelect, ModalButtons, ModalField } from './Modal';
import { dayMonth, n0, useToast } from './kit';
import type { Bus, FleetAlert, FleetModel, Refill } from './data/model';
import { REVIEW_REASON_COPY } from '../fleet/alertMeta';
import { correctRefuelEvent, originalOf, RefuelCorrectionError } from '../services/refuelCorrection';

interface Form { litres: string; cost: string; odometer: string; reason: string }

const shown = (n: number | null) => (n == null ? '' : String(Math.round(n * 10) / 10));
const formOf = (r: Refill | undefined): Form => ({ litres: shown(r?.litres ?? null), cost: r?.amount != null ? String(Math.round(r.amount)) : '', odometer: r?.odometer != null ? String(Math.round(r.odometer)) : '', reason: '' });
const label = (r: Refill) => [r.at ? dayMonth(r.at) : 'No date', r.litres != null ? `${shown(r.litres)} L` : 'litres not given', r.amount != null ? `Ksh ${n0(r.amount)}` : null, r.odometer != null ? `${n0(r.odometer)} km` : null].filter(Boolean).join(' · ');

export const RefillModal: React.FC<{ f: FleetModel; bus: Bus; alert: FleetAlert | null; onClose: () => void }> = ({ f, bus, alert, onClose }) => {
  const { toast } = useToast();
  // This bus's refills, newest first, without the ones its driver took back.
  const refills = useMemo(() => f.refills.filter((r) => r.busId === bus.id && r.raw.voided !== true), [f.refills, bus.id]);
  // The alert names no refill; the one logged nearest its time is the one it is about.
  const nearest = useMemo(() => {
    const at = alert?.at?.getTime();
    if (at == null) return refills[0];
    return [...refills].sort((a, b) => Math.abs((a.at?.getTime() ?? 0) - at) - Math.abs((b.at?.getTime() ?? 0) - at))[0];
  }, [refills, alert]);
  const [id, setId] = useState(nearest?.id || '');
  const sel = refills.find((r) => r.id === id);
  const [form, setForm] = useState<Form>(() => formOf(nearest));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The readings either side, for the distances this reading also sets.
  const older = sel ? refills.filter((r) => (r.at?.getTime() ?? 0) < (sel.at?.getTime() ?? 0) && r.odometer != null && r.odometer > 0) : [];
  const newer = sel ? refills.filter((r) => (r.at?.getTime() ?? 0) > (sel.at?.getTime() ?? 0) && r.odometer != null && r.odometer > 0) : [];
  const prev = older[0] || null;
  const next = newer[newer.length - 1] || null;
  const was = sel ? originalOf(sel.raw) : null;
  const why = alert ? (REVIEW_REASON_COPY[String(alert.raw.reviewReason || '')] || null) : null;

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = k === 'reason' ? e.target.value : e.target.value.replace(k === 'litres' ? /[^\d.]/g : /[^\d]/g, '');
    setForm((x) => ({ ...x, [k]: v }));
    setErr(null);
  };

  const submit = () => {
    if (busy || !sel) return;
    const before = formOf(sel);
    const changed = (k: 'litres' | 'cost' | 'odometer') => form[k].trim() !== '' && form[k].trim() !== before[k];
    const odometer = changed('odometer') ? Number(form.odometer) : undefined;
    setBusy(true);
    correctRefuelEvent(sel.id, {
      litersAdded: changed('litres') ? Number(form.litres) : undefined,
      costEstimate: changed('cost') ? Number(form.cost) : undefined,
      odometerKm: odometer,
      reason: form.reason,
    }, 'busRefuelEvents', {
      prevOdometerKm: prev?.odometer ?? null,
      next: next ? { id: next.id, odometerKm: next.odometer as number } : null,
      // The bus's own reading follows only when it came from this refill.
      busId: odometer !== undefined && !next && bus.raw.odometerKm === sel.odometer ? bus.id : null,
    })
      .then(() => {
        toast({ kind: 'success', text: `Refill corrected for ${bus.name}. The driver’s figures are kept beside yours.` });
        if (alert && !alert.read) void f.markAlertRead(alert.id).catch(() => undefined);
        onClose();
      })
      .catch((e: unknown) => setErr(e instanceof RefuelCorrectionError ? e.message : 'Couldn’t save the correction. Check the connection and try again.'))
      .finally(() => setBusy(false));
  };

  return (
    <ConsoleModal title="Correct the refill" onClose={onClose} onSubmit={submit} enterHint="save"
      footer={<ModalButtons onCancel={onClose} primary="Save correction" onPrimary={submit} busy={busy} disabled={!sel || !form.reason.trim()} />}>
      <p style={{ margin: '-6px 0 0', color: 'var(--muted)', fontSize: 15 }}>
        {bus.name}{why ? <> · flagged because {why}.</> : alert ? <> · {alert.title}</> : null}
      </p>
      {refills.length === 0 ? (
        <p style={{ margin: 0 }}>No refills on file for this bus yet, so there is nothing to correct.</p>
      ) : (
        <>
          <ModalField label="Refill">
            {(fid, ring) => (
              <select id={fid} {...ring} style={fieldSelect} value={id} onChange={(e) => { setId(e.target.value); setForm(formOf(refills.find((r) => r.id === e.target.value))); setErr(null); }}>
                {refills.slice(0, 12).map((r) => <option key={r.id} value={r.id}>{label(r)}</option>)}
              </select>
            )}
          </ModalField>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 140px', minWidth: 0, display: 'flex' }}>
              <ModalField label="Litres">
                {(fid, ring) => <input id={fid} {...ring} style={fieldInput} inputMode="decimal" value={form.litres} placeholder="50" onChange={set('litres')} />}
              </ModalField>
            </div>
            <div style={{ flex: '1 1 140px', minWidth: 0, display: 'flex' }}>
              <ModalField label="Cost (Ksh)">
                {(fid, ring) => <input id={fid} {...ring} style={fieldInput} inputMode="numeric" value={form.cost} placeholder="11300" onChange={set('cost')} />}
              </ModalField>
            </div>
          </div>
          <ModalField label="Odometer at this refill (km)"
            hint={[prev ? `Fill before: ${n0(prev.odometer as number)} km` : null, next ? `fill after: ${n0(next.odometer as number)} km` : null].filter(Boolean).join(' · ') || undefined}>
            {(fid, ring) => <input id={fid} {...ring} style={fieldInput} inputMode="numeric" value={form.odometer} placeholder="118400" onChange={set('odometer')} />}
          </ModalField>
          <ModalField label="Why it was wrong">
            {(fid, ring) => <input id={fid} {...ring} style={fieldInput} value={form.reason} maxLength={300} placeholder="Receipt says 50 L, the driver typed 5" onChange={set('reason')} />}
          </ModalField>
          {was && (
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13 }}>
              Corrected before. The driver first said {[was.litersAdded != null ? `${shown(was.litersAdded)} L` : null, was.costEstimate != null ? `Ksh ${n0(was.costEstimate)}` : null, was.odometerKm != null ? `${n0(was.odometerKm)} km` : null].filter(Boolean).join(', ') || 'nothing more'}.
            </p>
          )}
          {err && <span role="alert" style={{ color: 'var(--bad)', fontWeight: 600, fontSize: 14 }}>{err}</span>}
        </>
      )}
    </ConsoleModal>
  );
};
